import { resolveQmsDashboardDateRange } from "./qmsDashboardUtils";
import { normalizeComplianceRow } from "./qmsComplianceReportUtils";
import {
  normalizeArchivedDocumentRow,
  normalizePendingWorkflowRow,
  normalizeTrendDocumentRow,
} from "./qmsDocumentReportUtils";
import {
  isTimePeriodField,
  sortPivotRowsChronologically,
} from "./qmsReportIntelligence";
import { getVaultDocumentListId } from "./qmsVaultUtils";

export const QMS_REPORT_DATASETS = {
  compliance: {
    key: "compliance",
    label: "Reading & compliance",
    description: "Published document assignments, acknowledgements, and quiz status.",
  },
  tracker: { key: "tracker", label: "QMS tracker", description: "Document workflow tracker records." },
  readingList: { key: "readingList", label: "Reading list", description: "Organisation reading list." },
  changeRequests: { key: "changeRequests", label: "Change requests", description: "Change requests in your queue." },
  transferOwnership: { key: "transferOwnership", label: "Transfer ownership", description: "Ownership transfer queue." },
  archiveDocuments: { key: "archiveDocuments", label: "Archived documents", description: "Archived document register." },
};

export const QMS_COMPLIANCE_FIELDS = [
  { key: "documentId", label: "ID", type: "dimension" },
  { key: "staffName", label: "Staff", type: "dimension" },
  { key: "readStatus", label: "Status", type: "dimension" },
  { key: "marksObtained", label: "Marks obtained", type: "dimension" },
  { key: "acknowledgementDate", label: "Acknowledged", type: "date" },
  { key: "publishedDate", label: "Assigned date", type: "date", optional: true },
  { key: "documentType", label: "Document type", type: "dimension", optional: true },
  { key: "documentCategory", label: "Document category", type: "dimension", optional: true },
  { key: "versionNumber", label: "Version number", type: "dimension", optional: true },
];

export const QMS_PENDING_FIELDS = [
  { key: "documentId", label: "ID", type: "dimension" },
  { key: "assigneeName", label: "Assignee", type: "dimension" },
  { key: "ownerName", label: "Owner", type: "dimension" },
  { key: "sentDate", label: "Sent date", type: "date" },
  { key: "daysPending", label: "Days pending", type: "number" },
  { key: "documentType", label: "Document type", type: "dimension", optional: true },
  { key: "documentCategory", label: "Document category", type: "dimension", optional: true },
  { key: "versionNumber", label: "Version", type: "dimension", optional: true },
];

export const QMS_TREND_FIELDS = [
  { key: "documentId", label: "ID", type: "dimension" },
  { key: "ownerName", label: "Owner", type: "dimension" },
  { key: "eventDate", label: "Date", type: "date" },
  { key: "periodMonth", label: "Month", type: "dimension" },
  { key: "documentType", label: "Document type", type: "dimension", optional: true },
  { key: "documentCategory", label: "Document category", type: "dimension", optional: true },
  { key: "versionNumber", label: "Version", type: "dimension", optional: true },
];

export const QMS_ARCHIVED_FIELDS = [
  { key: "documentId", label: "ID", type: "dimension" },
  { key: "ownerName", label: "Owner", type: "dimension" },
  { key: "archivedDate", label: "Archived date", type: "date" },
  { key: "documentType", label: "Document type", type: "dimension", optional: true },
  { key: "documentCategory", label: "Document category", type: "dimension", optional: true },
  { key: "versionNumber", label: "Version", type: "dimension", optional: true },
];

export const QMS_DOCUMENT_HIDDEN_ADD_COLUMNS = ["documentTitle"];

export const QMS_COMPLIANCE_HIDDEN_ADD_COLUMNS = ["documentTitle", "roles"];

export const QMS_COMPLIANCE_OPTIONAL_TABLE_FIELDS = [
  "publishedDate",
  "documentType",
  "documentCategory",
  "versionNumber",
];

function formatCompliancePercentage(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  const rounded = Math.round(num * 10) / 10;
  return Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(1);
}

export function getComplianceMarksObtainedParts(row) {
  if (!row?.isQuizApplicable) return null;

  const correct = Number(row.quizCorrectCount);
  const total = Number(row.quizQuestionCount);
  const obtainedPct = Number(row.userPercentage);
  const requiredPct = Number(row.passingPercentage);

  const hasAttempt =
    (row.quizCorrectCount != null && row.quizCorrectCount !== "") ||
    (row.userPercentage != null && row.userPercentage !== "");

  let requiredMarks = Number.isFinite(total) && total > 0 ? Math.round(total) : null;
  const requiredPctLabel =
    Number.isFinite(requiredPct) && requiredPct >= 0
      ? `${formatCompliancePercentage(requiredPct)}%`
      : null;

  if (!hasAttempt) {
    if (!requiredMarks && !requiredPctLabel) return null;
    return {
      marksPart: requiredMarks ? `-/${requiredMarks}` : "—",
      pctPart: requiredPctLabel ? `-/${requiredPctLabel}` : "—",
    };
  }

  let displayCorrect = Number.isFinite(correct) && correct >= 0 ? Math.round(correct) : null;

  if (
    displayCorrect != null &&
    Number.isFinite(obtainedPct) &&
    obtainedPct > 0 &&
    (!requiredMarks || requiredMarks <= 0)
  ) {
    const derivedTotal = Math.round((displayCorrect / obtainedPct) * 100);
    if (derivedTotal > 0 && derivedTotal >= displayCorrect) {
      requiredMarks = derivedTotal;
    }
  }

  if (displayCorrect != null && requiredMarks && displayCorrect > requiredMarks) {
    displayCorrect = requiredMarks;
  }

  const marksObtained = displayCorrect != null ? String(displayCorrect) : "—";
  const marksRequired = requiredMarks ? String(requiredMarks) : "—";

  let obtainedPctLabel = "—";
  if (Number.isFinite(obtainedPct) && obtainedPct >= 0) {
    obtainedPctLabel = `${formatCompliancePercentage(obtainedPct)}%`;
  } else if (displayCorrect != null && requiredMarks) {
    obtainedPctLabel = `${formatCompliancePercentage((displayCorrect / requiredMarks) * 100)}%`;
  }

  const pctRequired = requiredPctLabel || "—";

  return {
    marksPart: `${marksObtained}/${marksRequired}`,
    pctPart: `${obtainedPctLabel}/${pctRequired}`,
  };
}

export function formatComplianceMarksObtained(row) {
  const parts = getComplianceMarksObtainedParts(row);
  if (!parts) return "—";
  if (parts.marksPart === "—" && parts.pctPart === "—") return "—";
  return `${parts.marksPart}  ${parts.pctPart}`;
}

export function getComplianceTableFieldOptions(fieldCatalog = QMS_COMPLIANCE_FIELDS) {
  const hidden = new Set(QMS_COMPLIANCE_HIDDEN_ADD_COLUMNS);
  return fieldCatalog.filter((field) => !hidden.has(field.key));
}

export function getDocumentTableFieldOptions(fieldCatalog) {
  const hidden = new Set(QMS_DOCUMENT_HIDDEN_ADD_COLUMNS);
  return (fieldCatalog || []).filter((field) => !hidden.has(field.key));
}

export function formatComplianceNameWithAbbr(name, abbr) {
  const label = String(name || "").trim();
  const short = String(abbr || "").trim();
  if (!label || label === "—") return "—";
  if (!short || short === label) return label;
  return `${label} (${short})`;
}

export function getComplianceReadStatusDisplay(row) {
  if (!row) {
    return { status: "—", isRead: false, hasQuiz: false, quizNote: "" };
  }
  const isRead = row.readStatus === "Read" || row.isRead === 1;
  const hasQuiz = Boolean(row.isQuizApplicable);
  let quizNote = "";
  if (hasQuiz) {
    quizNote = isRead ? "Quiz passed" : "Quiz required";
  }
  return {
    status: isRead ? "Read" : "Unread",
    isRead,
    hasQuiz,
    quizNote,
  };
}

export function matchComplianceReadStatusSearch(row, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;

  const meta = getComplianceReadStatusDisplay(row);
  const status = meta.status.toLowerCase();
  const quizNote = meta.quizNote.toLowerCase();

  if (needle === "read") return status === "read";
  if (needle === "unread") return status === "unread";
  if (needle === "quiz") return meta.hasQuiz;

  if (status.startsWith(needle)) return true;
  if (quizNote && quizNote.includes(needle)) return true;

  return false;
}

export function getComplianceTableCellValue(row, field) {
  if (!row) return "—";
  if (field === "documentId") {
    const id = row.documentId ?? "—";
    const title = row.documentTitle ?? "—";
    if (!title || title === "—") return id;
    return `${id}\n${title}`;
  }
  if (field === "readStatus") {
    const meta = getComplianceReadStatusDisplay(row);
    return meta.quizNote ? `${meta.status}\n${meta.quizNote}` : meta.status;
  }
  if (field === "staffName") {
    const name = row.staffName || "—";
    const emp = String(row.employeeNum || "").trim();
    return emp ? `[${emp}] ${name}` : name;
  }
  if (field === "marksObtained") {
    return formatComplianceMarksObtained(row);
  }
  if (field === "documentType") {
    return formatComplianceNameWithAbbr(row.documentType, row.documentTypeAbbr);
  }
  if (field === "documentCategory") {
    return formatComplianceNameWithAbbr(row.documentCategory, row.documentCategoryAbbr);
  }
  return row[field] ?? "—";
}

export const QMS_MEASURE_OPTIONS = [
  { value: "count:documentIdRef", label: "Count assignments", agg: "count", field: "documentIdRef" },
  { value: "sum:isRead", label: "Sum read", agg: "sum", field: "isRead" },
  { value: "count:staffName", label: "Count staff", agg: "count", field: "staffName" },
];

export const QMS_BUILDER_INITIAL = {
  dataset: "compliance",
  rows: ["staffName"],
  columns: [],
  measure: "count:documentIdRef",
  dimensionFilters: [],
};

export const QMS_FILTER_INITIAL = {
  dateRange: "All Time",
  dateFrom: "",
  dateTo: "",
  staff: "All",
  owner: "All",
  assignee: "All",
  role: "All",
  documentType: "All",
  documentCategory: "All",
  readStatus: "All",
};

const FALLBACK_CHART_COLORS = ["#243f5a", "#0d9488", "#6366f1", "#d97706", "#dc2626", "#0891b2"];

function toDisplayDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function camelKey(key) {
  return String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function normalizeGenericRow(row, index) {
  if (!row || typeof row !== "object") return null;
  const normalized = { __rowId: String(index) };
  Object.keys(row).forEach((key) => {
    const val = row[key];
    if (val == null || typeof val === "object") return;
    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    normalized[camel] = val;
    normalized[key] = val;
  });
  return normalized;
}

export function normalizeComplianceRecord(row) {
  const base = normalizeComplianceRow(row);
  if (!base) return null;
  const title = `${base.documentTitle || "Untitled"}${base.documentExtension ? `.${base.documentExtension}` : ""}`;
  const documentId = getVaultDocumentListId({
    DocumentTypeAbberiviation: base.documentTypeAbbr,
    DocumentCategoryAbberiviation: base.documentCategoryAbbr,
    DocumentCategoryUniqueNumber: base.documentCategoryUniqueNumber,
    VersionNumber: base.versionNumber,
    DocumentId: base.documentIdRef,
  });
  return {
    dataset: "compliance",
    documentIdRef: base.documentIdRef,
    documentId,
    documentTitle: title,
    documentUrl: base.documentUrl || "",
    documentExtension: base.documentExtension || "",
    documentType: base.documentType || "—",
    documentTypeAbbr: base.documentTypeAbbr || "",
    documentCategory: base.documentCategory || "—",
    documentCategoryAbbr: base.documentCategoryAbbr || "",
    versionNumber: base.versionNumber || "—",
    documentCategoryUniqueNumber: base.documentCategoryUniqueNumber || "",
    staffName: base.staffName || "—",
    employeeNum: base.employeeNum || "",
    roles: base.roles || "—",
    staffId: base.staffId,
    roleId: base.roleId,
    documentTypeId: base.documentTypeId,
    documentCategoryId: base.documentCategoryId,
    readStatus: base.isAcknowledged ? "Read" : "Unread",
    isQuizApplicable: base.isQuizApplicable,
    isPassed: base.isPassed,
    quizCorrectCount: base.quizCorrectCount,
    quizQuestionCount: base.quizQuestionCount,
    userPercentage: base.userPercentage,
    passingPercentage: base.passingPercentage,
    marksObtained: formatComplianceMarksObtained({
      isQuizApplicable: base.isQuizApplicable,
      quizCorrectCount: base.quizCorrectCount,
      quizQuestionCount: base.quizQuestionCount,
      userPercentage: base.userPercentage,
      passingPercentage: base.passingPercentage,
    }),
    publishedDate: base.publishedDateTime || "—",
    publishedAt: base.publishedDateTime,
    acknowledgementDate: base.acknowledgementDateTime || "—",
    isRead: base.isAcknowledged ? 1 : 0,
  };
}

export function normalizePendingRecord(row, datasetKey) {
  return normalizePendingWorkflowRow(row, datasetKey);
}

export function normalizeTrendRecord(row, datasetKey) {
  return normalizeTrendDocumentRow(row, datasetKey);
}

export function normalizeArchivedRecord(row) {
  return normalizeArchivedDocumentRow(row);
}

export function normalizeDatasetRows(datasetKey, rawRows) {
  if (datasetKey === "compliance") {
    return (rawRows || []).map(normalizeComplianceRecord).filter(Boolean);
  }
  if (datasetKey === "pendingApproval") {
    return (rawRows || []).map((row) => normalizePendingRecord(row, "pendingApproval")).filter(Boolean);
  }
  if (datasetKey === "pendingReview") {
    return (rawRows || []).map((row) => normalizePendingRecord(row, "pendingReview")).filter(Boolean);
  }
  if (datasetKey === "documentCreationTrend") {
    return (rawRows || []).map((row) => normalizeTrendRecord(row, "documentCreationTrend")).filter(Boolean);
  }
  if (datasetKey === "documentArchivingTrend") {
    return (rawRows || []).map((row) => normalizeTrendRecord(row, "documentArchivingTrend")).filter(Boolean);
  }
  if (datasetKey === "archivedDocuments") {
    return (rawRows || []).map(normalizeArchivedRecord).filter(Boolean);
  }
  return (rawRows || []).map(normalizeGenericRow).filter(Boolean);
}

export function getReportFieldCatalog(datasetKey) {
  if (datasetKey === "compliance") return QMS_COMPLIANCE_FIELDS;
  if (datasetKey === "pendingApproval" || datasetKey === "pendingReview") return QMS_PENDING_FIELDS;
  if (datasetKey === "documentCreationTrend" || datasetKey === "documentArchivingTrend") return QMS_TREND_FIELDS;
  if (datasetKey === "archivedDocuments") return QMS_ARCHIVED_FIELDS;
  return [];
}

export function inferFieldCatalog(rows, datasetKey) {
  const preset = getReportFieldCatalog(datasetKey);
  if (preset.length) return preset;
  if (!rows?.length) return [];
  const sample = rows[0];
  return Object.keys(sample)
    .filter((key) => !key.startsWith("__") && key !== "dataset")
    .slice(0, 24)
    .map((key) => ({
      key,
      label: camelKey(key),
      type: typeof sample[key] === "number" ? "number" : "dimension",
    }));
}

export function getFieldMeta(fieldCatalog, key) {
  return fieldCatalog.find((field) => field.key === key);
}

function normalizeMulti(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && value !== "All") return [value];
  return [];
}

function inDateRange(row, filters, datasetKey) {
  const range = filters.dateRange;
  if (range === "All Time" && !filters.dateFrom && !filters.dateTo) return true;
  const raw =
    row.publishedAt ||
    row.publishedDate ||
    row.sentAt ||
    row.eventAt ||
    row.archivedAt ||
    row.createdAt ||
    row.CreatedAt;
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime())) {
    if (
      datasetKey === "documentCreationTrend" ||
      datasetKey === "documentArchivingTrend" ||
      datasetKey === "pendingApproval" ||
      datasetKey === "pendingReview"
    ) {
      return false;
    }
    return range === "All Time";
  }

  if (filters.dateFrom || filters.dateTo) {
    const resolved = resolveQmsDashboardDateRange({
      preset: "Custom",
      startDate: filters.dateFrom,
      endDate: filters.dateTo,
    });
    const start = resolved.startDate ? new Date(`${resolved.startDate}T00:00:00`) : null;
    const end = resolved.endDate ? new Date(`${resolved.endDate}T23:59:59`) : null;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  }

  const resolved = resolveQmsDashboardDateRange({ preset: range });
  if (!resolved.startDate || !resolved.endDate) return true;
  const start = new Date(`${resolved.startDate}T00:00:00`);
  const end = new Date(`${resolved.endDate}T23:59:59`);
  return d >= start && d <= end;
}

export function applyReportFilters(rows, filters, datasetKey) {
  return (rows || []).filter((row) => {
    const dateFilteredDatasets = new Set([
      "compliance",
      "pendingApproval",
      "pendingReview",
      "documentCreationTrend",
      "documentArchivingTrend",
      "archivedDocuments",
    ]);
    if (dateFilteredDatasets.has(datasetKey) && !inDateRange(row, filters, datasetKey)) return false;

    const staffFilters = normalizeMulti(filters.staff);
    if (
      staffFilters.length &&
      !staffFilters.includes(row.staffName) &&
      !staffFilters.includes(row.staffId) &&
      !staffFilters.includes(row.assigneeName) &&
      !staffFilters.includes(row.assigneeId) &&
      !staffFilters.includes(row.ownerName) &&
      !staffFilters.includes(row.ownerId)
    ) {
      return false;
    }
    const ownerFilters = normalizeMulti(filters.owner);
    if (
      ownerFilters.length &&
      !ownerFilters.includes(row.ownerName) &&
      !ownerFilters.includes(row.ownerId)
    ) {
      return false;
    }
    const assigneeFilters = normalizeMulti(filters.assignee);
    if (
      assigneeFilters.length &&
      !assigneeFilters.includes(row.assigneeName) &&
      !assigneeFilters.includes(row.assigneeId)
    ) {
      return false;
    }
    const roleFilters = normalizeMulti(filters.role);
    if (roleFilters.length && !roleFilters.includes(row.roles) && !roleFilters.includes(row.roleId)) {
      return false;
    }
    const typeFilters = normalizeMulti(filters.documentType);
    if (typeFilters.length && !typeFilters.includes(row.documentType) && !typeFilters.includes(row.documentTypeId)) {
      return false;
    }
    const categoryFilters = normalizeMulti(filters.documentCategory);
    if (
      categoryFilters.length &&
      !categoryFilters.includes(row.documentCategory) &&
      !categoryFilters.includes(row.documentCategoryId)
    ) {
      return false;
    }
    if (datasetKey === "compliance" && filters.readStatus !== "All" && row.readStatus !== filters.readStatus) {
      return false;
    }
    return true;
  });
}

function parseMeasure(measureValue) {
  const [agg, field] = String(measureValue || "count:documentIdRef").split(":");
  return { agg: agg || "count", field: field || "documentIdRef" };
}

export function aggregateValues(values, agg) {
  const nums = values.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  if (agg === "sum") return nums.reduce((a, b) => a + b, 0);
  if (agg === "avg") return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
  if (agg === "distinct") return new Set(values.map(String)).size;
  return values.length;
}

export function buildCompliancePivotSummary(pivot) {
  return (pivot.rowKeys || []).map((rowKey) => {
    const rowMap = pivot.matrix.get(rowKey);
    const read = Number(rowMap?.get("Read") ?? 0);
    const unread = Number(rowMap?.get("Unread") ?? 0);
    const total = read + unread;
    const completedPct = total ? Math.round((read / total) * 100) : 0;
    const remainingPct = total ? 100 - completedPct : 0;
    return {
      rowKey,
      read,
      unread,
      total,
      completedPct,
      remainingPct,
    };
  });
}

export function buildGenericPivotSummary(pivot, groupField) {
  const rows = (pivot.rowKeys || []).map((rowKey) => {
    const rowMap = pivot.matrix.get(rowKey);
    const total = (pivot.colKeys || []).reduce(
      (sum, colKey) => sum + Number(rowMap?.get(colKey) || 0),
      0,
    );
    return { rowKey, total };
  });

  const sorted = isTimePeriodField(groupField)
    ? sortPivotRowsChronologically(rows, groupField)
    : rows.sort((a, b) => b.total - a.total || String(a.rowKey).localeCompare(String(b.rowKey)));

  const grandTotal = sorted.reduce((sum, row) => sum + row.total, 0);
  return sorted.map((row) => ({
    ...row,
    sharePct: grandTotal ? Math.round((row.total / grandTotal) * 100) : 0,
  }));
}

export function runPivotQuery(rows, builder, fieldCatalog) {
  const rowField = builder.rows?.[0];
  const colField = builder.columns?.[0];
  const { agg, field: valueField } = parseMeasure(builder.measure);

  if (!rowField) {
    return { rowKeys: [], colKeys: [], matrix: new Map(), rowField: null, colField: null, measure: { agg, field: valueField } };
  }

  const rowKeys = Array.from(new Set(rows.map((r) => String(r[rowField] ?? "—")))).sort();
  const colKeys = colField
    ? Array.from(new Set(rows.map((r) => String(r[colField] ?? "—")))).sort()
    : ["Total"];

  const matrix = new Map();
  rowKeys.forEach((rowKey) => {
    const inner = new Map();
    colKeys.forEach((colKey) => {
      const scoped = rows.filter((row) => {
        const rowMatch = String(row[rowField] ?? "—") === rowKey;
        const colMatch = colField ? String(row[colField] ?? "—") === colKey : true;
        return rowMatch && colMatch;
      });
      const values =
        agg === "count"
          ? scoped.map(() => 1)
          : scoped.map((row) => row[valueField]).filter((v) => v != null && v !== "");
      inner.set(colKey, aggregateValues(values, agg === "count" ? "count" : agg));
    });
    matrix.set(rowKey, inner);
  });

  return {
    rowKeys,
    colKeys,
    matrix,
    rowField,
    colField,
    measure: { agg, field: valueField },
    rowLabel: getFieldMeta(fieldCatalog, rowField)?.label || rowField,
    colLabel: colField ? getFieldMeta(fieldCatalog, colField)?.label || colField : "Total",
  };
}

export function buildComplianceStackedChartData(rows) {
  const sortedRows = [...(rows || [])].sort((a, b) => {
    if (b.unread !== a.unread) return b.unread - a.unread;
    if (b.total !== a.total) return b.total - a.total;
    return String(a.rowKey).localeCompare(String(b.rowKey));
  });
  return {
    rows: sortedRows,
    labels: sortedRows.map((row) => row.rowKey),
    readValues: sortedRows.map((row) => row.read),
    unreadValues: sortedRows.map((row) => row.unread),
    totals: sortedRows.map((row) => row.total),
    totalGroups: sortedRows.length,
  };
}

export const QMS_COMPLIANCE_CHART_COLORS = {
  read: "#16a34a",
  unread: "#cbd5e1",
};

export function buildChartFromPivot(pivot) {
  const labels = pivot.rowKeys.slice(0, 16);
  const values = labels.map((rowKey) => {
    const rowMap = pivot.matrix.get(rowKey);
    return (pivot.colKeys || []).reduce((sum, colKey) => sum + Number(rowMap?.get(colKey) || 0), 0);
  });
  const colors = labels.map((_, index) => FALLBACK_CHART_COLORS[index % FALLBACK_CHART_COLORS.length]);
  return { labels, values, colors };
}

export function buildDocumentCountChartData(rows, groupField = "periodMonth") {
  const counts = new Map();
  (rows || []).forEach((row) => {
    const key = String(row[groupField] || "—");
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const sorted = Array.from(counts.entries()).sort((a, b) => {
    if (isTimePeriodField(groupField)) return String(a[0]).localeCompare(String(b[0]));
    return b[1] - a[1] || String(a[0]).localeCompare(String(b[0]));
  });
  return {
    labels: sorted.map(([label]) => label),
    values: sorted.map(([, value]) => value),
    colors: sorted.map((_, index) => FALLBACK_CHART_COLORS[index % FALLBACK_CHART_COLORS.length]),
    totalGroups: sorted.length,
    rows: sorted.map(([label, count]) => ({ label, count })),
  };
}

export function buildDocumentTrendChartData(rows) {
  return buildDocumentCountChartData(rows, "periodMonth");
}

export function buildDocumentGroupedBarChartData(rows, groupField) {
  return buildDocumentCountChartData(rows, groupField);
}

export function getDocumentTableCellValue(row, field) {
  if (!row) return "—";
  if (field === "documentId") {
    const id = row.documentId ?? "—";
    const title = row.documentTitle ?? "—";
    if (!title || title === "—") return id;
    return `${id}\n${title}`;
  }
  if (field === "documentType") {
    return formatComplianceNameWithAbbr(row.documentType, row.documentTypeAbbr);
  }
  if (field === "documentCategory") {
    return formatComplianceNameWithAbbr(row.documentCategory, row.documentCategoryAbbr);
  }
  if (field === "daysPending") {
    return row.daysPending == null || Number.isNaN(Number(row.daysPending)) ? "—" : String(row.daysPending);
  }
  return row[field] ?? "—";
}

export function buildReportSummary(rows, datasetKey) {
  if (datasetKey === "compliance") {
    const read = rows.filter((r) => r.isRead === 1).length;
    const unread = rows.length - read;
    const staff = new Set(rows.map((r) => r.staffName)).size;
    const pct = rows.length ? Math.round((read / rows.length) * 100) : 0;
    return [
      { label: "Assignments", value: rows.length },
      { label: "Read", value: read },
      { label: "Unread", value: unread },
      { label: "Completion", value: `${pct}%` },
      { label: "Staff", value: staff },
    ];
  }
  if (datasetKey === "pendingApproval" || datasetKey === "pendingReview") {
    const assignees = new Set(rows.map((r) => r.assigneeName)).size;
    const owners = new Set(rows.map((r) => r.ownerName)).size;
    const overdue = rows.filter((r) => Number(r.daysPending) > 7).length;
    return [
      { label: "Pending items", value: rows.length },
      { label: "Assignees", value: assignees },
      { label: "Owners", value: owners },
      { label: "> 7 days", value: overdue },
    ];
  }
  if (datasetKey === "documentCreationTrend" || datasetKey === "documentArchivingTrend") {
    const months = new Set(rows.map((r) => r.periodMonth)).size;
    return [
      { label: "Documents", value: rows.length },
      { label: "Months", value: months },
      { label: "Types", value: new Set(rows.map((r) => r.documentType)).size },
      { label: "Categories", value: new Set(rows.map((r) => r.documentCategory)).size },
    ];
  }
  if (datasetKey === "archivedDocuments") {
    return [
      { label: "Archived", value: rows.length },
      { label: "Types", value: new Set(rows.map((r) => r.documentType)).size },
      { label: "Categories", value: new Set(rows.map((r) => r.documentCategory)).size },
      { label: "Owners", value: new Set(rows.map((r) => r.ownerName)).size },
    ];
  }
  return [
    { label: "Rows", value: rows.length },
    { label: "Dataset", value: QMS_REPORT_DATASETS[datasetKey]?.label || datasetKey },
  ];
}

export function buildFilterOptions(rows, datasetKey) {
  const uniq = (values) =>
    Array.from(new Set(values.filter(Boolean))).sort().map((value) => ({ value, label: value }));

  if (datasetKey === "compliance") {
    return {
      staff: uniq(rows.map((r) => r.staffName)),
      owner: [],
      assignee: [],
      roles: uniq(rows.map((r) => r.roles)),
      documentTypes: uniq(rows.map((r) => r.documentType)),
      documentCategories: uniq(rows.map((r) => r.documentCategory)),
      readStatuses: [
        { value: "Read", label: "Read" },
        { value: "Unread", label: "Unread" },
      ],
    };
  }

  if (datasetKey === "pendingApproval" || datasetKey === "pendingReview") {
    return {
      staff: [],
      owner: uniq(rows.map((r) => r.ownerName)),
      assignee: uniq(rows.map((r) => r.assigneeName)),
      roles: [],
      documentTypes: uniq(rows.map((r) => r.documentType)),
      documentCategories: uniq(rows.map((r) => r.documentCategory)),
      readStatuses: [],
    };
  }

  if (datasetKey === "documentCreationTrend" || datasetKey === "documentArchivingTrend") {
    return {
      staff: [],
      owner: uniq(rows.map((r) => r.ownerName)),
      assignee: [],
      roles: [],
      documentTypes: uniq(rows.map((r) => r.documentType)),
      documentCategories: uniq(rows.map((r) => r.documentCategory)),
      readStatuses: [],
    };
  }

  if (datasetKey === "archivedDocuments") {
    return {
      staff: [],
      owner: uniq(rows.map((r) => r.ownerName)),
      assignee: [],
      roles: [],
      documentTypes: uniq(rows.map((r) => r.documentType)),
      documentCategories: uniq(rows.map((r) => r.documentCategory)),
      readStatuses: [],
    };
  }

  return {
    staff: [],
    owner: [],
    assignee: [],
    roles: [],
    documentTypes: [],
    documentCategories: [],
    readStatuses: [],
  };
}

export const QMS_REPORT_DATASET_OPTIONS = [
  { value: "compliance", label: "Compliance" },
  { value: "pendingApproval", label: "Pending for approval" },
  { value: "pendingReview", label: "Pending for review" },
  { value: "documentCreationTrend", label: "Document Creation trend" },
  { value: "documentArchivingTrend", label: "Document Archiving trend" },
  { value: "archivedDocuments", label: "Archived Document" },
];

export const QMS_COMPLIANCE_GROUP_BY_OPTIONS = [
  { value: "staff", label: "Staff", rowField: "staffName" },
  { value: "role", label: "Role", rowField: "roles" },
  { value: "category", label: "Category", rowField: "documentCategory" },
  { value: "type", label: "Type", rowField: "documentType" },
];

export const QMS_PENDING_GROUP_BY_OPTIONS = [
  { value: "assignee", label: "Assignee", rowField: "assigneeName" },
  { value: "owner", label: "Owner", rowField: "ownerName" },
  { value: "category", label: "Category", rowField: "documentCategory" },
  { value: "type", label: "Type", rowField: "documentType" },
];

export const QMS_TREND_GROUP_BY_OPTIONS = [
  { value: "day", label: "Day", rowField: "periodDay", isTimeBucket: true },
  { value: "week", label: "Week", rowField: "periodWeek", isTimeBucket: true },
  { value: "month", label: "Month", rowField: "periodMonth", isTimeBucket: true },
  { value: "quarter", label: "Quarter", rowField: "periodQuarter", isTimeBucket: true },
  { value: "halfYear", label: "6 Month", rowField: "periodHalfYear", isTimeBucket: true },
  { value: "year", label: "Year", rowField: "periodYear", isTimeBucket: true },
  { value: "type", label: "Type", rowField: "documentType" },
  { value: "category", label: "Category", rowField: "documentCategory" },
  { value: "owner", label: "Owner", rowField: "ownerName" },
];

export const QMS_ARCHIVED_GROUP_BY_OPTIONS = [
  { value: "month", label: "Month", rowField: "periodMonth", isTimeBucket: true },
  { value: "type", label: "Type", rowField: "documentType" },
  { value: "category", label: "Category", rowField: "documentCategory" },
  { value: "owner", label: "Owner", rowField: "ownerName" },
];

export const QMS_REPORT_TABLE_DEFAULTS = {
  compliance: ["documentId", "staffName", "readStatus", "marksObtained", "acknowledgementDate"],
  pendingApproval: ["documentId", "assigneeName", "ownerName", "sentDate", "daysPending"],
  pendingReview: ["documentId", "assigneeName", "ownerName", "sentDate", "daysPending"],
  documentCreationTrend: ["documentId", "ownerName", "eventDate", "documentType", "documentCategory"],
  documentArchivingTrend: ["documentId", "ownerName", "eventDate", "documentType", "documentCategory"],
  archivedDocuments: ["documentId", "ownerName", "archivedDate", "documentType", "documentCategory"],
};

export function getReportGroupByOptions(datasetKey = "compliance") {
  if (datasetKey === "compliance") return QMS_COMPLIANCE_GROUP_BY_OPTIONS;
  if (datasetKey === "pendingApproval" || datasetKey === "pendingReview") return QMS_PENDING_GROUP_BY_OPTIONS;
  if (datasetKey === "documentCreationTrend" || datasetKey === "documentArchivingTrend") {
    return QMS_TREND_GROUP_BY_OPTIONS;
  }
  if (datasetKey === "archivedDocuments") return QMS_ARCHIVED_GROUP_BY_OPTIONS;
  return [];
}

export function getReportGroupByOption(datasetKey = "compliance", value = "staff") {
  const options = getReportGroupByOptions(datasetKey);
  return options.find((item) => item.value === value) || options[0];
}

export function getDefaultGroupBy(datasetKey) {
  if (datasetKey === "compliance") return "staff";
  if (datasetKey === "pendingApproval" || datasetKey === "pendingReview") return "assignee";
  if (datasetKey === "documentCreationTrend" || datasetKey === "documentArchivingTrend") return "month";
  if (datasetKey === "archivedDocuments") return "type";
  return "staff";
}

export function isComplianceReport(datasetKey) {
  return datasetKey === "compliance";
}

export function isTrendReport(datasetKey) {
  return datasetKey === "documentCreationTrend" || datasetKey === "documentArchivingTrend";
}

export function getDefaultCanvasView() {
  return "pivot";
}

export function supportsPivotView() {
  return true;
}

const COMPLIANCE_REPORT_BUILDER_BASE = {
  dataset: "compliance",
  columns: ["readStatus"],
  measure: "count:documentIdRef",
  dimensionFilters: [],
};

export function getComplianceReportDataset(value = "compliance") {
  return QMS_REPORT_DATASET_OPTIONS.find((item) => item.value === value) || QMS_REPORT_DATASET_OPTIONS[0];
}

export function getReportDataset(value = "compliance") {
  return getComplianceReportDataset(value);
}

export function buildDocumentReportBuilder(datasetKey, groupBy) {
  const groupOption = getReportGroupByOption(datasetKey, groupBy);
  if (datasetKey === "compliance") {
    return buildComplianceReportBuilder(groupBy);
  }
  return {
    dataset: datasetKey,
    rows: [groupOption?.rowField || "documentType"],
    columns: [],
    measure: "count:documentIdRef",
    dimensionFilters: [],
  };
}

export function getReportExportTitle(datasetKey = "compliance", groupBy) {
  const dataset = getReportDataset(datasetKey);
  const group = getReportGroupByOption(datasetKey, groupBy);
  if (!group) return dataset.label;
  return `${dataset.label} - ${group.label}`;
}

export function getComplianceGroupByOption(value = "staff") {
  return QMS_COMPLIANCE_GROUP_BY_OPTIONS.find((item) => item.value === value) || QMS_COMPLIANCE_GROUP_BY_OPTIONS[0];
}

export function buildComplianceReportBuilder(groupBy = "staff") {
  const groupOption = getComplianceGroupByOption(groupBy);
  return {
    ...COMPLIANCE_REPORT_BUILDER_BASE,
    rows: [groupOption.rowField],
  };
}

export function getComplianceReportExportTitle(datasetKey = "compliance", groupBy = "staff") {
  return getReportExportTitle(datasetKey, groupBy);
}

/** @deprecated Use report dataset + group by options instead */
export const QMS_COMPLIANCE_REPORT_TYPES = QMS_COMPLIANCE_GROUP_BY_OPTIONS.map((group) => ({
  value: `by-${group.value}`,
  label: `Group by ${group.label}`,
  builder: buildComplianceReportBuilder(group.value),
}));

/** @deprecated Use getComplianceReportDataset + getComplianceGroupByOption */
export function getComplianceReportType(value) {
  const legacyGroup = String(value || "").replace(/^by-/, "");
  const groupBy = getComplianceGroupByOption(legacyGroup).value;
  return {
    value,
    label: getComplianceReportExportTitle("compliance", groupBy),
    builder: buildComplianceReportBuilder(groupBy),
  };
}

export const QMS_QUICK_REPORTS = [
  {
    key: "compliance-by-staff",
    category: "Compliance",
    label: "Group by Staff",
    description: "Reading completion grouped by staff member.",
    apply: () => ({
      dataset: "compliance",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "compliance", rows: ["staffName"], columns: ["readStatus"], measure: "count:documentIdRef", dimensionFilters: [] },
    }),
  },
  {
    key: "compliance-by-role",
    category: "Compliance",
    label: "Group by Role",
    description: "Completion rates across staff roles.",
    apply: () => ({
      dataset: "compliance",
      canvasView: "chart",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "compliance", rows: ["roles"], columns: [], measure: "count:documentIdRef", dimensionFilters: [] },
    }),
  },
  {
    key: "compliance-by-type",
    category: "Compliance",
    label: "Group by Document Type",
    description: "Volume and completion by document type.",
    apply: () => ({
      dataset: "compliance",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "compliance", rows: ["documentType"], columns: ["readStatus"], measure: "count:documentIdRef", dimensionFilters: [] },
    }),
  },
  {
    key: "unread-queue",
    category: "Compliance",
    label: "Unread assignments",
    description: "All unread reading assignments.",
    apply: () => ({
      dataset: "compliance",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL, readStatus: "Unread" },
      builder: { dataset: "compliance", rows: ["staffName"], columns: ["documentTitle"], measure: "count:documentIdRef", dimensionFilters: [] },
    }),
  },
  {
    key: "quiz-documents",
    category: "Compliance",
    label: "Quiz applicable",
    description: "Assignments where a quiz is required.",
    apply: () => ({
      dataset: "compliance",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "compliance", rows: ["documentTitle"], columns: ["readStatus"], measure: "count:documentIdRef", dimensionFilters: [] },
    }),
  },
  {
    key: "compliance-detail",
    category: "Compliance",
    label: "Assignment register",
    description: "Full flat list for export and audit.",
    apply: () => ({
      dataset: "compliance",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "compliance", rows: ["documentTitle"], columns: [], measure: "count:documentIdRef", dimensionFilters: [] },
    }),
  },
  {
    key: "tracker-register",
    category: "Operations",
    label: "QMS tracker",
    description: "Workflow tracker register.",
    apply: () => ({
      dataset: "tracker",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "tracker", rows: [], columns: [], measure: "count:__rowId", dimensionFilters: [] },
    }),
  },
  {
    key: "reading-list",
    category: "Operations",
    label: "Reading list",
    description: "Organisation reading list export.",
    apply: () => ({
      dataset: "readingList",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "readingList", rows: [], columns: [], measure: "count:__rowId", dimensionFilters: [] },
    }),
  },
  {
    key: "change-requests",
    category: "Operations",
    label: "Change requests",
    description: "Open change requests in your queue.",
    apply: () => ({
      dataset: "changeRequests",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "changeRequests", rows: [], columns: [], measure: "count:__rowId", dimensionFilters: [] },
    }),
  },
  {
    key: "transfer-ownership",
    category: "Operations",
    label: "Transfer ownership",
    description: "Documents pending ownership transfer.",
    apply: () => ({
      dataset: "transferOwnership",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "transferOwnership", rows: [], columns: [], measure: "count:__rowId", dimensionFilters: [] },
    }),
  },
  {
    key: "archive-documents",
    category: "Operations",
    label: "Archived documents",
    description: "Archived document register.",
    apply: () => ({
      dataset: "archiveDocuments",
      canvasView: "table",
      chartType: "bar",
      filters: { ...QMS_FILTER_INITIAL },
      builder: { dataset: "archiveDocuments", rows: [], columns: [], measure: "count:__rowId", dimensionFilters: [] },
    }),
  },
];

export function pickFirstField(fieldCatalog, type = "dimension") {
  return fieldCatalog.find((field) => field.type === type)?.key || fieldCatalog[0]?.key || "";
}

export function defaultTableColumns(fieldCatalog, datasetKey) {
  if (datasetKey === "compliance") {
    return ["documentId", "staffName", "readStatus", "marksObtained", "acknowledgementDate"];
  }
  return fieldCatalog.slice(0, 6).map((field) => field.key);
}
